<template>
  <div class="mt-2">
    <b-breadcrumb class="breadcrumb-pipes">
      <b-breadcrumb-item href="/settings/admin-panel/">Admin Panel</b-breadcrumb-item>
      <b-breadcrumb-item active>Manage Onboarding Documents</b-breadcrumb-item>
    </b-breadcrumb>

    <b-card title="Manage Onboarding Necessary Documents">
      <b-overlay :show="loading">
        <p class="text-muted">
          Documents listed here are requested automatically on the client onboarding form when the
          client type picked on the form and the settlement type of the account both match.
          A document with no client type is never requested automatically — it stays available to be
          requested by hand from the onboarding detail page.
        </p>

        <b-row align-h="between" class="mb-1">
          <b-col cols="12" md="3">
            <b-form-input v-model="filter" placeholder="Search..." />
          </b-col>
          <b-col cols="12" md="3" class="d-flex justify-content-end">
            <b-button variant="success" @click="openCreateModal">
              Create New Document
            </b-button>
          </b-col>
        </b-row>

        <b-table
          striped
          hover
          responsive
          :items="documents"
          :filter="filter"
          :fields="tableColumns"
          :current-page="currentPage"
          :per-page="perPage"
          show-empty
          empty-text="No onboarding documents defined yet."
          @filtered="onFiltered"
        >
          <template #cell(index)="data">
            <strong class="text-primary">#{{ data.index + 1 }}</strong>
          </template>

          <template #cell(ClientTypes)="{ value }">
            <b-badge v-for="type in value || []" :key="type" variant="light-primary" class="mr-25">
              {{ type }}
            </b-badge>
            <span v-if="!(value || []).length" class="text-muted">Manual only</span>
          </template>

          <template #cell(SettlementTypes)="{ value }">
            <b-badge v-for="type in value || []" :key="type" variant="light-info" class="mr-25">
              {{ type }}
            </b-badge>
            <span v-if="!(value || []).length" class="text-muted">—</span>
          </template>

          <template #cell(PerAuthorisedTrader)="{ value }">
            <feather-icon
              :icon="value ? 'CheckSquareIcon' : 'SquareIcon'"
              :class="value ? 'text-success' : 'text-muted'"
            />
          </template>

          <template #cell(PerNonDealingSignatory)="{ value }">
            <feather-icon
              :icon="value ? 'CheckSquareIcon' : 'SquareIcon'"
              :class="value ? 'text-success' : 'text-muted'"
            />
          </template>

          <template #cell(PerAppointedTrader)="{ value }">
            <feather-icon
              :icon="value ? 'CheckSquareIcon' : 'SquareIcon'"
              :class="value ? 'text-success' : 'text-muted'"
            />
          </template>

          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="flat-primary"
              class="btn-icon"
              @click="openEditModal(data.item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <b-button
              size="sm"
              variant="flat-danger"
              class="btn-icon"
              @click="confirmDelete(data.item)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </template>
        </b-table>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
        />
      </b-overlay>
    </b-card>

    <!-- Create / Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="form.id ? 'Edit Onboarding Document' : 'Create Onboarding Document'"
      size="lg"
      ok-title="Save"
      :ok-disabled="modalLoading"
      no-close-on-backdrop
      @ok="handleSave"
    >
      <b-overlay :show="modalLoading">
        <b-form>
          <b-form-group label="Document Title">
            <b-form-input v-model="form.Name" placeholder="e.g. Certified copy of passport" />
          </b-form-group>

          <b-form-group label="Client Type">
            <b-form-checkbox-group v-model="form.ClientTypes" :options="clientTypes" />
            <small class="text-muted">
              The client types this document is required for, as selected on the onboarding form.
              Company and Partnership belong to accounts with a <strong>CORPORATE</strong> holder
              type and Individual to <strong>INDIVIDUAL</strong> ones;
              <strong>COUNTERPARTY</strong> accounts are never asked for documents.
            </small>
          </b-form-group>

          <b-form-group label="Settlement Type">
            <b-form-checkbox-group v-model="form.SettlementTypes" :options="settlementTypes" />
            <small class="text-muted">
              The settlement types of the account, as set on the account creation page.
            </small>
          </b-form-group>

          <b-form-group label="Asked for each person">
            <b-form-checkbox v-model="form.PerAuthorisedTrader" class="mb-50">
              For each person / trader authorised to give instructions
            </b-form-checkbox>
            <b-form-checkbox v-model="form.PerNonDealingSignatory" class="mb-50">
              For each signatory authorised to instruct non-dealing matters
            </b-form-checkbox>
            <b-form-checkbox v-model="form.PerAppointedTrader" class="mb-50">
              For each appointed trader (Appointed Traders' Knowledge and Experience)
            </b-form-checkbox>
            <small class="text-muted">
              Each ticked section gets its own upload box under every person added there.
              With none ticked the document is asked once for the account.
            </small>
          </b-form-group>

          <hr />

          <b-form-group label="Apply this change to">
            <b-form-radio-group v-model="form.applyToExisting" stacked>
              <b-form-radio :value="false">
                New onboardings only
                <small class="text-muted d-block">
                  The {{ existingOnboardingCount }} onboarding(s) already under way keep the
                  documents they were started with.
                </small>
              </b-form-radio>
              <b-form-radio :value="true">
                New and existing onboardings
                <small class="text-muted d-block">
                  Also updates the {{ existingOnboardingCount }} onboarding(s) already under way,
                  including forms clients have already signed.
                </small>
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </b-form>
      </b-overlay>
    </b-modal>

    <!-- Delete Modal -->
    <b-modal
      v-model="showDeleteModal"
      title="Delete Onboarding Document"
      ok-title="Delete"
      ok-variant="danger"
      no-close-on-backdrop
      @ok="handleDelete"
    >
      <p>
        Delete <strong>{{ deleteTarget ? deleteTarget.Name : "" }}</strong>?
        It will no longer be requested on new onboarding forms.
      </p>
      <b-form-group label="Apply this change to">
        <b-form-radio-group v-model="deleteApplyToExisting" stacked>
          <b-form-radio :value="false">
            New onboardings only
            <small class="text-muted d-block">
              Clients already asked for this document are still asked for it.
            </small>
          </b-form-radio>
          <b-form-radio :value="true">
            New and existing onboardings
            <small class="text-muted d-block">
              Also withdraws the request from the {{ existingOnboardingCount }} onboarding(s)
              already under way. Documents clients have already uploaded are kept.
            </small>
          </b-form-radio>
        </b-form-radio-group>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BTable, BPagination, BBadge,
  BFormInput, BFormGroup, BForm, BFormCheckbox, BFormCheckboxGroup,
  BFormRadio, BFormRadioGroup, BModal, BBreadcrumb, BBreadcrumbItem, BOverlay,
} from 'bootstrap-vue'
import store from '@/store'
import { onUnmounted } from '@vue/composition-api'
import settingsStoreModule from '../../settingsStoreModule'

function emptyForm() {
  return {
    id: null,
    Name: '',
    ClientTypes: [],
    SettlementTypes: [],
    PerAuthorisedTrader: false,
    PerNonDealingSignatory: false,
    PerAppointedTrader: false,
    // existing onboardings keep their own documents unless this is turned on
    applyToExisting: false,
  }
}

export default {
  components: {
    BCard, BRow, BCol, BButton, BTable, BPagination, BBadge,
    BFormInput, BFormGroup, BForm, BFormCheckbox, BFormCheckboxGroup,
    BFormRadio, BFormRadioGroup, BModal, BBreadcrumb, BBreadcrumbItem, BOverlay,
  },

  data() {
    return {
      loading: false,
      modalLoading: false,
      documents: [],
      clientTypes: [],
      settlementTypes: [],
      existingOnboardingCount: 0,
      filter: '',
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      showModal: false,
      showDeleteModal: false,
      deleteTarget: null,
      deleteApplyToExisting: false,
      form: emptyForm(),
      tableColumns: [
        { key: 'index', label: '' },
        { key: 'Name', label: 'Document Title', sortable: true },
        { key: 'ClientTypes', label: 'Client Type' },
        { key: 'SettlementTypes', label: 'Settlement Type' },
        { key: 'PerAuthorisedTrader', label: 'Per Authorised Trader', class: 'text-center' },
        { key: 'PerNonDealingSignatory', label: 'Per Signatory', class: 'text-center' },
        { key: 'PerAppointedTrader', label: 'Per Appointed Trader', class: 'text-center' },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },

  mounted() {
    this.loadDocuments()
  },

  methods: {
    loadDocuments() {
      this.loading = true
      store.dispatch('settings/getOnboardingDocumentSettings')
        .then(res => {
          this.documents = res.data.documents
          this.clientTypes = res.data.clientTypes
          this.settlementTypes = res.data.settlementTypes
          this.existingOnboardingCount = res.data.existingOnboardingCount
          this.totalRows = res.data.documents.length
        })
        .catch(() => {
          this.$bvToast.toast('Failed to load onboarding documents.', { variant: 'danger', title: 'Error' })
        })
        .finally(() => { this.loading = false })
    },

    openCreateModal() {
      this.form = emptyForm()
      this.showModal = true
    },

    openEditModal(item) {
      this.form = {
        id: item.id,
        Name: item.Name,
        ClientTypes: item.ClientTypes || [],
        SettlementTypes: item.SettlementTypes || [],
        PerAuthorisedTrader: !!item.PerAuthorisedTrader,
        PerNonDealingSignatory: !!item.PerNonDealingSignatory,
        PerAppointedTrader: !!item.PerAppointedTrader,
        applyToExisting: false,
      }
      this.showModal = true
    },

    handleSave(bvModalEvt) {
      bvModalEvt.preventDefault()

      if (!this.form.Name.trim()) {
        this.$bvToast.toast('Document title is required.', { variant: 'danger', title: 'Validation' })
        return
      }
      if (this.form.ClientTypes.length && !this.form.SettlementTypes.length) {
        this.$bvToast.toast(
          'Pick at least one settlement type, otherwise this document is never requested.',
          { variant: 'danger', title: 'Validation' },
        )
        return
      }

      this.modalLoading = true
      store.dispatch('settings/saveOnboardingDocumentSetting', {
        id: this.form.id,
        name: this.form.Name.trim(),
        clientTypes: this.form.ClientTypes,
        settlementTypes: this.form.SettlementTypes,
        perAuthorisedTrader: this.form.PerAuthorisedTrader,
        perNonDealingSignatory: this.form.PerNonDealingSignatory,
        perAppointedTrader: this.form.PerAppointedTrader,
        applyToExisting: this.form.applyToExisting,
      })
        .then(res => {
          this.$bvToast.toast(res.data.message, { variant: 'success', title: 'Success' })
          this.showModal = false
          this.loadDocuments()
        })
        .catch(err => {
          const message = (err.response && err.response.data && err.response.data.message) || 'An error occurred.'
          this.$bvToast.toast(message, { variant: 'danger', title: 'Error' })
        })
        .finally(() => { this.modalLoading = false })
    },

    confirmDelete(item) {
      this.deleteTarget = item
      this.deleteApplyToExisting = false
      this.showDeleteModal = true
    },

    handleDelete(bvModalEvt) {
      bvModalEvt.preventDefault()

      store.dispatch('settings/deleteOnboardingDocumentSetting', {
        id: this.deleteTarget.id,
        applyToExisting: this.deleteApplyToExisting,
      })
        .then(res => {
          this.$bvToast.toast(res.data.message, { variant: 'success', title: 'Deleted' })
          this.showDeleteModal = false
          this.loadDocuments()
        })
        .catch(() => {
          this.$bvToast.toast('An error occurred.', { variant: 'danger', title: 'Error' })
        })
    },

    onFiltered(items) {
      this.totalRows = items.length
      this.currentPage = 1
    },
  },

  setup() {
    const STORE_KEY = 'settings'
    if (!store.hasModule(STORE_KEY)) store.registerModule(STORE_KEY, settingsStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_KEY)) store.unregisterModule(STORE_KEY)
    })
  },
}
</script>
